import { useRef, useState } from "react";
import { Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CompanyType } from "../../models/company";
import { useAppDispatch } from "../../store";
import { companyActions } from "../../store/company-slice";
import { fetchCompanyFiles, patchCompany, uploadCompanyFile } from "../../utils/http/companies";

type PredefinedImageName = "logo" | "team";

const PREDEFINED_IMAGE_NAMES: { key: PredefinedImageName; label: string }[] = [
  { key: "logo", label: "Logo" },
  { key: "team", label: "Team" },
];

interface CompanyDocumentsProps {
  company: CompanyType;
  readOnly?: boolean;
}

function FileIcon({ contentType }: { contentType?: string | null }) {
  if (contentType?.startsWith("image/")) return null; // handled by <img>
  if (contentType === "application/pdf")
    return <i className="bi bi-file-earmark-pdf" style={{ fontSize: 48, color: "#dc3545" }} />;
  if (contentType?.startsWith("video/"))
    return <i className="bi bi-file-earmark-play" style={{ fontSize: 48, color: "#6c757d" }} />;
  if (
    contentType === "application/msword" ||
    contentType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  )
    return <i className="bi bi-file-earmark-word" style={{ fontSize: 48, color: "#2b579a" }} />;
  if (
    contentType === "application/vnd.ms-excel" ||
    contentType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  )
    return <i className="bi bi-file-earmark-excel" style={{ fontSize: 48, color: "#217346" }} />;
  return <i className="bi bi-file-earmark" style={{ fontSize: 48, color: "#6c757d" }} />;
}

function CompanyDocuments({ company, readOnly = false }: CompanyDocumentsProps) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);

  const companyImages = company.config_design_temp?.images;

  function getAssignedName(fileUrl: string): PredefinedImageName | "" {
    if (!companyImages) return "";
    const stripQs = (u: string) => u.split("?")[0];
    const stripped = stripQs(fileUrl);
    for (const { key } of PREDEFINED_IMAGE_NAMES) {
      const val = companyImages[key];
      if (val && stripQs(val) === stripped) return key;
    }
    return "";
  }

  async function handleAssignName(fileUrl: string, name: PredefinedImageName | "") {
    const currentImages = company.config_design_temp?.images ?? {};
    const updatedImages = { ...currentImages };

    // Remove this URL from any existing assignment
    for (const { key } of PREDEFINED_IMAGE_NAMES) {
      const val = updatedImages[key];
      if (val && val.split("?")[0] === fileUrl.split("?")[0]) {
        updatedImages[key] = undefined;
      }
    }

    // Assign to the new name (if not clearing)
    if (name) {
      updatedImages[name] = `${fileUrl.split("?")[0]}?t=${Date.now()}`;
    }

    try {
      const updated = await patchCompany({
        companyNameOrId: company.name,
        patch: { config_design_temp: { ...company.config_design_temp, images: updatedImages } },
      });
      dispatch(companyActions.setActiveCompany(updated));
      queryClient.invalidateQueries({ queryKey: ["company", company.name] });
    } catch {
      // silent
    }
  }

  const { data: files = [], isLoading } = useQuery({
    queryKey: ["company-files", company.name],
    queryFn: ({ signal }) => fetchCompanyFiles({ companyNameOrId: company.name, signal }),
  });

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    if (files.length) processFiles(files);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) processFiles(files);
  };

  const processFiles = async (files: File[]) => {
    setUploading(true);
    setUploadError(null);
    try {
      await Promise.all(files.map((file) => uploadCompanyFile({ companyNameOrId: company.name, file })));
      await queryClient.invalidateQueries({ queryKey: ["company-files", company.name] });
    } catch (e: any) {
      setUploadError(e?.info?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    });
  };

  return (
    <div>
      {!readOnly && (
        <>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
            multiple
            style={{ display: "none" }}
          />
          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            onDrop={(e) => { if (!uploading) handleDrop(e); }}
            onDragOver={handleDragOver}
            style={{
              border: "2px dashed #dee2e6",
              borderRadius: "8px",
              padding: "24px",
              textAlign: "center",
              cursor: uploading ? "default" : "pointer",
              marginBottom: "16px",
              transition: "border-color 0.2s",
              opacity: uploading ? 0.6 : 1,
            }}
            onMouseEnter={(e) => { if (!uploading) e.currentTarget.style.borderColor = "#0049ff"; }}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#dee2e6")}
          >
            {uploading ? (
              <div>
                <Spinner animation="border" size="sm" className="text-muted" />
                <div className="text-muted mt-2" style={{ fontSize: "0.8rem" }}>Uploading…</div>
              </div>
            ) : (
              <div>
                <i className="bi bi-cloud-upload" style={{ fontSize: 28, color: "#6c757d" }} />
                <div className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>
                  Click or drag a file here to upload
                </div>
                <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                  Images, videos, PDFs, Office documents, CSV, ZIP
                </div>
              </div>
            )}
          </div>
          {uploadError && (
            <div className="alert alert-danger py-2" style={{ fontSize: "0.85rem" }}>
              {uploadError}
            </div>
          )}
        </>
      )}

      {isLoading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="text-muted" />
        </div>
      ) : files.length === 0 ? (
        <div className="text-muted" style={{ fontSize: "0.85rem" }}>No files uploaded yet.</div>
      ) : (
        <Row className="g-3">
          {files.map((file) => (
            <Col key={file.path} xs={6} sm={4} md={3}>
              <div
                className="border rounded p-2 text-center h-100 d-flex flex-column align-items-center justify-content-between"
                style={{ gap: 6 }}
              >
                <div
                  style={{
                    width: 80,
                    height: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                >
                  {file.content_type?.startsWith("image/") ? (
                    <img
                      src={file.public_url}
                      alt={file.name}
                      style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 4, cursor: "zoom-in" }}
                      onClick={() => setPreviewImage({ url: file.public_url, name: file.name })}
                    />
                  ) : (
                    <FileIcon contentType={file.content_type} />
                  )}
                </div>
                <div
                  className="text-truncate w-100"
                  title={file.name}
                  style={{ fontSize: "0.75rem", color: "#495057" }}
                >
                  {file.name}
                </div>
                {file.content_type?.startsWith("image/") && (
                  <Form.Select
                    size="sm"
                    value={getAssignedName(file.public_url)}
                    onChange={(e) => handleAssignName(file.public_url, e.target.value as PredefinedImageName | "")}
                    disabled={readOnly}
                    style={{ fontSize: "0.7rem", padding: "2px 6px" }}
                  >
                    <option value="">--</option>
                    {PREDEFINED_IMAGE_NAMES.map(({ key, label }) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </Form.Select>
                )}
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm w-100"
                  style={{ fontSize: "0.7rem", padding: "2px 6px" }}
                  onClick={() => handleCopy(file.public_url)}
                  title="Copy URL"
                >
                  <i className={`bi ${copiedUrl === file.public_url ? "bi-clipboard-check" : "bi-clipboard"}`} />
                  {" "}
                  {copiedUrl === file.public_url ? "Copied!" : "Copy URL"}
                </button>
              </div>
            </Col>
          ))}
        </Row>
      )}
      <Modal show={!!previewImage} onHide={() => setPreviewImage(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "0.95rem", wordBreak: "break-all" }}>
            {previewImage?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-2">
          {previewImage && (
            <img
              src={previewImage.url}
              alt={previewImage.name}
              style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain" }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default CompanyDocuments;
